/**
 * GoBingeBox — Shared API contract types.
 *
 * These describe the JSON shapes exchanged between the web app and the API.
 * They intentionally mirror (but are decoupled from) the Drizzle DB schema in
 * apps/api/src/db/schema.ts. Keep field names aligned with the schema.
 */

import type { BillingPeriod, TierId } from './tiers';

export type { BillingPeriod, TierId } from './tiers';

/* ---------------------------------------------------------------- users --- */

export type Role = 'user' | 'contributor' | 'admin' | 'owner';

export interface User {
  id: string;
  email: string;
  displayName: string;
  emailVerified: boolean;
  role: Role;
  birthdate: string | null; // YYYY-MM-DD
  preferences: UserPreferences;
  createdAt: string; // ISO 8601
}

/** The current viewer's session + entitlement snapshot. */
export interface Viewer {
  user: User | null;
  /** Resolved access: can this viewer watch full episodes right now? */
  hasFullAccess: boolean;
  /** Why access was granted, for UI/debugging. */
  accessSource: 'none' | 'subscription' | 'comp' | 'staff';
  subscription: Subscription | null;
  /** Netflix-style profiles under this account. */
  profiles: Profile[];
  activeProfileId: string | null;
  /** Unread in-app notification count (for the header badge). */
  unreadNotifications: number;
}

/** A selectable profile under one account (its own history + list). */
export interface Profile {
  id: string;
  name: string;
  avatarColor: string;
  isKids: boolean;
  createdAt: string;
}

/** Avatar colors offered in the profile picker. */
export const PROFILE_AVATAR_COLORS = [
  '#2563eb', '#4f46e5', '#0891b2', '#7c3aed',
  '#059669', '#d97706', '#e11d48', '#475569',
] as const;

export const MAX_PROFILES = 5;

/* -------------------------------------------------------------- comments --- */

export type CommentStatus = 'visible' | 'held' | 'removed';
/** What the auto-filter does when it catches a blocked word. */
export type CommentFilterMode = 'mask' | 'block' | 'hold';

export interface Comment {
  id: string;
  showId: string;
  authorName: string;
  body: string;
  likes: number;
  dislikes: number;
  status: CommentStatus;
  /** The viewer's reaction: 1 like, -1 dislike, 0 none. */
  myReaction: -1 | 0 | 1;
  /** True if the viewer authored this comment. */
  mine: boolean;
  createdAt: string;
}

/** Comment as seen in the Dev Panel (adds author identity for moderation). */
export interface AdminComment extends Comment {
  userId: string;
  authorEmail: string;
  showTitle: string;
}

/* ----------------------------------------------------- in-app notifications --- */

export type NotificationKind = 'show' | 'premiere' | 'recommendation' | 'system';

export interface AppNotification {
  id: string;
  kind: NotificationKind;
  title: string;
  body: string;
  link: string | null;
  read: boolean;
  createdAt: string;
}

/* -------------------------------------------------------- subscriptions --- */

export type SubscriptionStatus =
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'incomplete';

export type SubscriptionSource = 'stripe' | 'comp';

export interface Subscription {
  id: string;
  userId: string;
  tier: TierId;
  billingPeriod: BillingPeriod;
  /** Purchased simultaneous-stream count (playback limit). */
  streams: number;
  status: SubscriptionStatus;
  source: SubscriptionSource;
  autoRenew: boolean;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null; // ISO 8601
  createdAt: string;
}

/* ------------------------------------------------------------- catalog --- */

export type ShowStatus = 'upcoming' | 'ongoing' | 'completed';
export type AudioLanguage = 'ja' | 'en';

/** Age/content rating (spec §2 owner request). */
export type ContentRating = 'E' | '13' | '16' | '18';

export const CONTENT_RATINGS: { value: ContentRating; label: string; minAge: number }[] = [
  { value: 'E', label: 'All Ages (E)', minAge: 0 },
  { value: '13', label: '13+', minAge: 13 },
  { value: '16', label: '16+', minAge: 16 },
  { value: '18', label: '18+', minAge: 18 },
];

/** Minimum age required for a rating (0 if unrated). */
export function minAgeForRating(rating: ContentRating | null): number {
  return CONTENT_RATINGS.find((r) => r.value === rating)?.minAge ?? 0;
}

/** Whole-year age from an ISO/`YYYY-MM-DD` birthdate. */
export function ageFromBirthdate(birthdate: string): number {
  const dob = new Date(birthdate);
  if (Number.isNaN(dob.getTime())) return 0;
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age -= 1;
  return age;
}

/** Minimum age to create an account. */
export const MIN_SIGNUP_AGE = 13;

/** Per-account viewing preferences (spec §settings). */
export interface UserPreferences {
  displayLanguage: 'en' | 'ja';
  audioLanguage: AudioLanguage;
  subtitleLanguage: 'off' | AudioLanguage;
  closedCaptions: boolean;
  audioDescriptions: boolean;
  /** Highest rating the account is allowed to watch ('none' = no limit). */
  contentRestriction: ContentRating | 'none';
}

export const DEFAULT_PREFERENCES: UserPreferences = {
  displayLanguage: 'en',
  audioLanguage: 'ja',
  subtitleLanguage: 'en',
  closedCaptions: false,
  audioDescriptions: false,
  contentRestriction: 'none',
};

export interface CastMember {
  name: string;
  role: string; // e.g. character name or "Director"
  kind: 'voice' | 'crew';
}

export interface Genre {
  id: string;
  slug: string;
  name: string;
}

export interface Show {
  id: string;
  slug: string;
  title: string;
  description: string;
  posterUrl: string | null;
  thumbnailUrl: string | null;
  heroUrl: string | null;
  previewStreamUid: string | null;
  cast: CastMember[];
  status: ShowStatus;
  rating: ContentRating | null;
  genres: Genre[];
  createdAt: string;
}

export interface Episode {
  id: string;
  showId: string;
  season: number;
  number: number;
  title: string;
  description: string;
  streamUid: string | null;
  thumbnailUrl: string | null;
  durationSeconds: number | null;
  audioLanguages: AudioLanguage[];
  /** null = unscheduled; future = "Coming {date}". ISO 8601. */
  releaseAt: string | null;
  createdAt: string;
}

export interface WatchProgress {
  episodeId: string;
  progressSeconds: number;
  updatedAt: string;
}

/* --------------------------------------------------------------- store --- */

export interface ProductVariant {
  id: string;
  name: string;
  sku: string | null;
  stock: number;
}

export interface Product {
  id: string;
  slug: string;
  name: string;
  description: string;
  priceCents: number;
  images: string[];
  stock: number;
  active: boolean;
  variants: ProductVariant[];
  createdAt: string;
}

export type OrderStatus = 'paid' | 'fulfilled' | 'refunded';

export interface ShippingAddress {
  name: string;
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export interface OrderItem {
  id: string;
  productId: string;
  variantId: string | null;
  qty: number;
  unitPriceCents: number;
}

export interface Order {
  id: string;
  userId: string | null;
  email: string;
  subtotalCents: number;
  discountCents: number;
  totalCents: number;
  shippingAddress: ShippingAddress;
  status: OrderStatus;
  items: OrderItem[];
  createdAt: string;
}

/* --------------------------------------------------------------- games --- */

export interface Game {
  id: string;
  slug: string;
  title: string;
  description: string;
  thumbnailUrl: string | null;
  embedUrl: string | null;
  rating: ContentRating | null;
  active: boolean;
  createdAt: string;
}

/* ------------------------------------------------- forums / submissions --- */

export type SubmissionType = 'upload_access' | 'show_idea';
export type SubmissionStatus = 'new' | 'reviewing' | 'accepted' | 'declined';

export interface Submission {
  id: string;
  type: SubmissionType;
  name: string;
  email: string;
  title: string;
  details: string;
  links: string | null;
  status: SubmissionStatus;
  createdAt: string;
}

/* ------------------------------------------------------- notifications --- */

export type NotificationTopic = 'store' | 'games' | 'new_episodes';

/* ---------------------------------------------------------------- misc --- */

/** Standard API error body. */
export interface ApiErrorBody {
  error: string;
  message?: string;
  details?: unknown;
}

/** Health/status payload (Phase 1). */
export interface HealthStatus {
  status: 'ok' | 'degraded';
  environment: string;
  time: string;
  db: 'ok' | 'error';
  version: string;
}
